import type { DailyTotalRow, UserChurnRow, CompanyRow, UserRosterRow } from "./sql";

export type Bucket = "daily" | "weekly" | "monthly";

export const fmtN = (n: number): string => Math.round(n).toLocaleString();
export const fmtPct = (n: number, d: number): string => {
  if (d === 0) return "0.0%";
  return `${((n / d) * 100).toFixed(1)}%`;
};
export const fmtPctDelta = (n: number): string => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

export function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d.getTime());
  copy.setUTCDate(copy.getUTCDate() + n);
  return copy;
}

export function startOfISOWeek(d: Date): Date {
  const copy = new Date(d.getTime());
  const day = copy.getUTCDay() || 7;
  copy.setUTCDate(copy.getUTCDate() - (day - 1));
  return copy;
}

export function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

export function bucketKey(isoDay: string, bucket: Bucket): string {
  const d = parseISODate(isoDay);
  if (bucket === "daily") return toISODate(d);
  if (bucket === "weekly") return toISODate(startOfISOWeek(d));
  return toISODate(startOfMonth(d));
}

export function bucketLabel(key: string, bucket: Bucket): string {
  const d = parseISODate(key);
  if (bucket === "monthly") {
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

export type BucketSeries = {
  key: string;
  label: string;
  meetings: number;
  transcripts: number;
  summaries: number;
  meetingChats: number;
  clientChats: number;
  conversionPct: number;
};

export function bucketize(rows: DailyTotalRow[], bucket: Bucket): BucketSeries[] {
  const agg = new Map<string, BucketSeries>();
  for (const r of rows) {
    const key = bucketKey(r.day, bucket);
    let entry = agg.get(key);
    if (!entry) {
      entry = {
        key,
        label: bucketLabel(key, bucket),
        meetings: 0, transcripts: 0, summaries: 0,
        meetingChats: 0, clientChats: 0, conversionPct: 0,
      };
      agg.set(key, entry);
    }
    entry.meetings += r.meetings;
    entry.transcripts += r.transcripts;
    entry.summaries += r.summaries;
    entry.meetingChats += r.ask_marloo_meeting_chats;
    entry.clientChats += r.ask_marloo_client_chats;
  }
  const out = Array.from(agg.values()).sort((a, b) => a.key.localeCompare(b.key));
  for (const e of out) {
    e.conversionPct = e.transcripts > 0 ? (e.summaries / e.transcripts) * 100 : 0;
  }
  return out;
}

export type ChurnRiskLevel = "High" | "Medium" | "Low";
export type ChurnStatus = "Active" | "Churned";

export type ChurnIndicator = {
  status: ChurnStatus;
  risk: ChurnRiskLevel;
  reasons: string[];
};

export type ChurnWindows = {
  meetings_r28: number;
  transcripts_r28: number;
  summaries_r28: number;
  chats_r28: number;
  meetings_p28: number;
  transcripts_p28: number;
  summaries_p28: number;
  chats_p28: number;
  chats_r14: number;
  chats_p14: number;
  meetings_r14: number;
};

const EMPTY_WINDOWS: ChurnWindows = {
  meetings_r28: 0, transcripts_r28: 0, summaries_r28: 0, chats_r28: 0,
  meetings_p28: 0, transcripts_p28: 0, summaries_p28: 0, chats_p28: 0,
  chats_r14: 0, chats_p14: 0, meetings_r14: 0,
};

function windowsFromUserRow(r: UserChurnRow): ChurnWindows {
  return {
    meetings_r28:    r.meetings_r28,
    transcripts_r28: r.transcripts_r28,
    summaries_r28:   r.summaries_r28,
    chats_r28:       r.meeting_chats_r28 + r.client_chats_r28,
    meetings_p28:    r.meetings_p28,
    transcripts_p28: r.transcripts_p28,
    summaries_p28:   r.summaries_p28,
    chats_p28:       r.chats_p28,
    chats_r14:       r.chats_r14,
    chats_p14:       r.chats_p14,
    meetings_r14:    r.meetings_r14,
  };
}

function addWindows(a: ChurnWindows, b: ChurnWindows): ChurnWindows {
  return {
    meetings_r28:    a.meetings_r28    + b.meetings_r28,
    transcripts_r28: a.transcripts_r28 + b.transcripts_r28,
    summaries_r28:   a.summaries_r28   + b.summaries_r28,
    chats_r28:       a.chats_r28       + b.chats_r28,
    meetings_p28:    a.meetings_p28    + b.meetings_p28,
    transcripts_p28: a.transcripts_p28 + b.transcripts_p28,
    summaries_p28:   a.summaries_p28   + b.summaries_p28,
    chats_p28:       a.chats_p28       + b.chats_p28,
    chats_r14:       a.chats_r14       + b.chats_r14,
    chats_p14:       a.chats_p14       + b.chats_p14,
    meetings_r14:    a.meetings_r14    + b.meetings_r14,
  };
}

const DECLINE_THRESHOLD_PCT = 15;
const ASK_MARLOO_DECLINE_THRESHOLD_PCT = 30;

export function computeIndicators(w: ChurnWindows): ChurnIndicator {
  const reasons: string[] = [];

  const recentTranscriptRate = w.meetings_r28 > 0 ? w.transcripts_r28 / w.meetings_r28 : 0;
  const priorTranscriptRate  = w.meetings_p28 > 0 ? w.transcripts_p28 / w.meetings_p28 : 0;
  if (
    priorTranscriptRate > 0 &&
    (priorTranscriptRate - recentTranscriptRate) / priorTranscriptRate * 100 >= DECLINE_THRESHOLD_PCT
  ) {
    reasons.push("Meeting to transcript conversion has declined");
  }

  if (w.summaries_p28 > 0 && w.summaries_r28 === 0) {
    reasons.push("Transcript to summary conversion has dropped to zero");
  } else {
    const recentSummaryRate = w.transcripts_r28 > 0 ? w.summaries_r28 / w.transcripts_r28 : 0;
    const priorSummaryRate  = w.transcripts_p28 > 0 ? w.summaries_p28 / w.transcripts_p28 : 0;
    if (
      priorSummaryRate > 0 &&
      (priorSummaryRate - recentSummaryRate) / priorSummaryRate * 100 >= DECLINE_THRESHOLD_PCT
    ) {
      reasons.push("Transcript to summary conversion has declined");
    }
  }

  const totalChats = w.chats_r28 + w.chats_p28;
  if (totalChats === 0) {
    reasons.push("Ask Marloo has never been used");
  } else if (
    w.chats_p14 > 0 &&
    (w.chats_p14 - w.chats_r14) / w.chats_p14 * 100 >= ASK_MARLOO_DECLINE_THRESHOLD_PCT
  ) {
    reasons.push("Ask Marloo usage has declined");
  }

  const status: ChurnStatus = w.meetings_r14 > 0 ? "Active" : "Churned";
  let risk: ChurnRiskLevel = "Low";
  if (reasons.length >= 2) risk = "High";
  else if (reasons.length === 1) risk = "Medium";

  return { status, risk, reasons };
}

export type CompanyIndicator = ChurnIndicator & {
  company_external_id: string;
  company_name: string;
  country_code: string | null;
  seats_max: number;
  billing_plan: string | null;
  is_paid: boolean;
};

export type UserIndicator = ChurnIndicator & {
  user_external_id: string;
  user_email: string;
  company_external_id: string;
  company_name: string;
  country_code: string | null;
  max_tier: string;
  is_paid: boolean;
};

export function isPaidPlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  const p = plan.toLowerCase();
  return p === "pro" || p === "plus";
}

export function buildCompanyIndicators(
  userChurnRows: UserChurnRow[],
  companies: CompanyRow[],
): CompanyIndicator[] {
  const byCompany = new Map<string, ChurnWindows>();
  for (const u of userChurnRows) {
    const existing = byCompany.get(u.company_external_id) ?? EMPTY_WINDOWS;
    byCompany.set(u.company_external_id, addWindows(existing, windowsFromUserRow(u)));
  }

  const out: CompanyIndicator[] = [];
  const seenNames = new Set<string>();
  for (const c of companies) {
    const key = c.company_name.toLowerCase();
    if (seenNames.has(key)) continue;
    seenNames.add(key);

    const windows = byCompany.get(c.company_external_id) ?? EMPTY_WINDOWS;
    out.push({
      ...computeIndicators(windows),
      company_external_id: c.company_external_id,
      company_name: c.company_name,
      country_code: c.country_code,
      seats_max: c.seats_max,
      billing_plan: c.billing_plan,
      is_paid: isPaidPlan(c.billing_plan) && !!c.stripe_subscription_id,
    });
  }
  return out;
}

export function buildUserIndicators(
  userChurnRows: UserChurnRow[],
  roster: UserRosterRow[],
): UserIndicator[] {
  const byUserCompany = new Map<string, UserChurnRow>();
  const countryByCompany = new Map<string, string | null>();
  for (const r of userChurnRows) {
    byUserCompany.set(`${r.company_external_id}|${r.user_external_id}`, r);
    if (!countryByCompany.has(r.company_external_id)) {
      countryByCompany.set(r.company_external_id, r.country_code);
    }
  }

  const planRank = (p: string | null | undefined): number => {
    const s = (p ?? "").toLowerCase();
    if (s === "pro") return 3;
    if (s === "plus") return 2;
    if (s === "free") return 1;
    return 0;
  };
  const bestPlanByUser = new Map<string, string>();
  for (const r of roster) {
    const existing = bestPlanByUser.get(r.user_external_id);
    if (!existing || planRank(r.billing_plan) > planRank(existing)) {
      bestPlanByUser.set(r.user_external_id, r.billing_plan ?? "");
    }
  }

  const out: UserIndicator[] = [];
  const seenKeys = new Set<string>();
  for (const r of roster) {
    const k = `${r.company_external_id}|${r.user_external_id}`;
    if (seenKeys.has(k)) continue;
    seenKeys.add(k);

    const churn = byUserCompany.get(k);
    const windows = churn ? windowsFromUserRow(churn) : EMPTY_WINDOWS;
    const maxTier = bestPlanByUser.get(r.user_external_id) || r.billing_plan || "free";
    out.push({
      ...computeIndicators(windows),
      user_external_id: r.user_external_id,
      user_email: r.user_email,
      company_external_id: r.company_external_id,
      company_name: r.company_name,
      country_code: churn?.country_code ?? countryByCompany.get(r.company_external_id) ?? null,
      max_tier: maxTier,
      is_paid: isPaidPlan(maxTier),
    });
  }
  return out;
}

export function rankLevel(level: ChurnRiskLevel): number {
  if (level === "High") return 3;
  if (level === "Medium") return 2;
  return 1;
}

export type Kpis = {
  distinctSummaries: number;
  summConvRecent: number;
  summConvDelta: number;
  meetingChats: number;
  clientChats: number;
};

export function computeKpisFromUserChurn(rows: UserChurnRow[]): Kpis {
  let summaries_r28 = 0, summaries_p28 = 0;
  let transcripts_r28 = 0, transcripts_p28 = 0;
  let meeting_chats_r28 = 0, client_chats_r28 = 0;
  for (const r of rows) {
    summaries_r28 += r.summaries_r28;
    summaries_p28 += r.summaries_p28;
    transcripts_r28 += r.transcripts_r28;
    transcripts_p28 += r.transcripts_p28;
    meeting_chats_r28 += r.meeting_chats_r28;
    client_chats_r28 += r.client_chats_r28;
  }
  const summConvRecent = transcripts_r28 > 0 ? (summaries_r28 / transcripts_r28) * 100 : 0;
  const summConvPrior  = transcripts_p28 > 0 ? (summaries_p28 / transcripts_p28) * 100 : 0;
  return {
    distinctSummaries: summaries_r28,
    summConvRecent,
    summConvDelta: summConvRecent - summConvPrior,
    meetingChats: meeting_chats_r28,
    clientChats: client_chats_r28,
  };
}
